import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { HStack, VStack, Text, Box, Flex } from '@cad-arc';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { OnLexicalError } from '../../common/components/lexical/types';
import { EditorControl } from '../../common/components/lexical';
import classNames from 'classnames';
import AdditionalInfoRenderer from '../additional-info-renderer/additional-info-renderer';
import { AdditionalInfoRow } from '../components/additional-info-row';
import { AdditionalInfosFilter } from './components/additional-infos-filter';
import CreateNarrativeForm from '../components/create-narrative-form';
import { InlineBanners } from '../components/inline-banners';
import { NarrativeJumpButtons } from '../components/narrative-jump-buttons';
import { NarrativeSortDirectionButton } from '../components/narrative-sort-direction-button';
import { UnreadButton } from '../components/unread-button';
import styles from '../components/narrative-panel.module.css';
import {
    AdditionalInfoNotificationManager,
    SortDirection,
    UnreadInfo,
} from '../hooks/use-additional-infos/types';
import { CrossClassInformation } from '../types';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import { FR_JUMP_BUTTONS_RESERVED_WIDTH } from '../constants';
import { useTogglePinnedMessagesShortcut } from '../hooks/use-toggle-pinned-messages-shortcut/use-toggle-pinned-messages-shortcut';

type DispatchReplaceVehiclesAction = ({
    values,
    replacementGroupId,
}: {
    values: { type: 'vehicle'; text: string }[];
    replacementGroupId: string;
}) => void;

/* eslint-disable-next-line */
export interface EventPanelAdditionalInfosProps {
    agencyEventId: number;
    agencyEventNumber: string; // used to show agency event number in the error alert
    crossClassInformation?: CrossClassInformation; // causes the cross class button to appear in the narrative panel
    currentUserId: number;
    dispatchReplaceVehicles?: DispatchReplaceVehiclesAction; // this is used by additional infos to update the event panel vehicle list
    firstUnreadInfo?: UnreadInfo; // this is used by additional infos to determine where to show the unread line initially
    focusInputRef?: React.MutableRefObject<(() => void) | undefined>; // this is used by parent components to focus the narrative input
    isPanelFocused?: boolean; // this is used to determine if the unread infos in view should be marked read
    isScheduledEvent?: boolean; // this configures additional infos panel for scheduled event
    isTransmittedEvent?: boolean; // this configures additional infos panel for transmitted event
    isFirstResponder?: boolean; // this does specific configurations and changes for First Responder
    isReadOnlyMode?: boolean; // this is for a First Responder 'Narrative' tab view
    onAllRead?: (id: number) => void; // this contains a function which will be executed to let the parent know that all unreads have been seen
    onLexicalError?: OnLexicalError; // this is triggered when the lexical component throws an error, and should be used by parent to emit the error to monitoring
    onOpenEvent?: (id: number) => void; // this contains a function which allows additional infos panel to open another panel or First Responder 'Event Info' tab view
    notificationManager?: AdditionalInfoNotificationManager; // Adds compatibility for controlling event notifications from different sources.
    forceReload?: boolean;
}

export function EventPanelAdditionalInfos({
    crossClassInformation,
    currentUserId,
    dispatchReplaceVehicles,
    focusInputRef,
    isPanelFocused = false,
    isScheduledEvent = false,
    isTransmittedEvent = false,
    isFirstResponder = false,
    isReadOnlyMode = false,
    onAllRead,
    onLexicalError,
    onOpenEvent,
    forceReload,
}: EventPanelAdditionalInfosProps): React.JSX.Element {
    const canCreateAdditionalInfos = useEnabledFeatures()?.['createAdditionalInfos'] ?? true;
    const canDeleteAdditionalInfos = useEnabledFeatures()?.['deleteAdditionalInfos'] ?? true;
    const narrativeFilteringEnabled =
        useEnabledFeatures()?.rollOutCadNarrativeFilteringAndFocusImprovementsEnabled ?? false;
    const { sortDirection } = useAdditionalInfos();
    const isAscLayout = sortDirection === SortDirection.ASC;
    const showDirectionToggle = narrativeFilteringEnabled && !isScheduledEvent && !isReadOnlyMode;
    const editorControlRef = useRef<EditorControl>();
    const jumpHandlersRef = useRef<{
        jumpToNewest: () => void;
        jumpToOldest: () => void;
    } | null>(null);
    useTogglePinnedMessagesShortcut(isFirstResponder);

    const handleJumpToNewest = useCallback(() => jumpHandlersRef.current?.jumpToNewest(), []);
    const handleJumpToOldest = useCallback(() => jumpHandlersRef.current?.jumpToOldest(), []);

    const onScrollToTop = isAscLayout ? handleJumpToOldest : handleJumpToNewest;
    const onScrollToBottom = isAscLayout ? handleJumpToNewest : handleJumpToOldest;

    const [jumpButtonEdgeState, setJumpButtonEdgeState] = useState({
        isAtTop: false,
        isAtBottom: false,
        isScrollable: false,
    });

    const showNarrativeForm = canCreateAdditionalInfos && !isScheduledEvent && !isReadOnlyMode;
    const showJumpButtons = narrativeFilteringEnabled && !isScheduledEvent;

    const additionalInfoRendererProps = {
        rowComponent: AdditionalInfoRow,
        addonComponent: UnreadButton,
        currentUserId,
        // CAD gates visible-as-read on the focused workspace panel. FR has no
        // panel-focus concept — the narrative being rendered IS the active
        // view — so it's always eligible there (RND-24882: notifications must
        // clear while actively viewing, without navigating away and back).
        // Deliberately not forcing isPanelFocused itself: that would also
        // trigger the editor auto-focus effect below, popping the keyboard on
        // FR mobile.
        markVisibleAsRead: isFirstResponder || isPanelFocused,
        canDeleteInfos: canDeleteAdditionalInfos && !isReadOnlyMode,
        isScheduledEvent,
        onOpenEvent,
        onAllRead,
        forceReload,
        isReadOnlyMode,
        isFirstResponder,
        focusNarrativeInput: () => editorControlRef.current?.focus(),
        jumpHandlersRef,
        onEdgeStateChange: setJumpButtonEdgeState,
        hasInlineJumpButtons: showJumpButtons && !isFirstResponder,
    };

    useEffect(() => {
        if (focusInputRef && editorControlRef.current) {
            focusInputRef.current = editorControlRef.current.focus;
        }
        if (isPanelFocused) {
            setTimeout(() => editorControlRef.current?.focus(), 0);
        }
    }, [editorControlRef, isPanelFocused, focusInputRef]);

    return (
        <VStack align="stretch" height="100%">
            <InlineBanners />
            {!isReadOnlyMode && (
                <Box paddingInlineStart="var(--arc-space-4)" paddingInlineEnd="4" paddingTop="2">
                    <HStack
                        justify={isFirstResponder ? 'flex-end' : 'space-between'}
                        className={classNames(
                            styles['event-panel-section-heading'],
                            !isFirstResponder && styles['event-panel-section-heading-height'],
                            (!isFirstResponder || isAscLayout) &&
                                styles['event-panel-section-heading-border']
                        )}
                    >
                        {!isFirstResponder && (
                            <Text className={styles['event-panel-section-heading-text']}>
                                Narrative
                            </Text>
                        )}
                        <HStack justify="end" spacing="var(--arc-space-4)">
                            {showDirectionToggle && <NarrativeSortDirectionButton />}
                            <AdditionalInfosFilter
                                isFirstResponder={isFirstResponder}
                                crossClassOptions={crossClassInformation?.crossClassOptions}
                            />
                        </HStack>
                    </HStack>
                </Box>
            )}
            {showNarrativeForm && (
                <Box
                    order={isAscLayout ? 2 : undefined}
                    paddingInlineStart={
                        isAscLayout && !isFirstResponder ? undefined : 'var(--arc-space-4)'
                    }
                    paddingInlineEnd={isAscLayout && !isFirstResponder ? undefined : '4'}
                    className={
                        isAscLayout && !isFirstResponder
                            ? styles['narrative-form-bottom']
                            : undefined
                    }
                >
                    <CreateNarrativeForm
                        editorControlRef={editorControlRef}
                        crossClassInformation={crossClassInformation}
                        onReplaceVehicles={(vehicles, narrativeId) => {
                            dispatchReplaceVehicles?.({
                                values: vehicles.map((vehicle) => ({
                                    type: 'vehicle',
                                    text: vehicle,
                                })),
                                replacementGroupId: narrativeId,
                            });
                        }}
                        onLexicalError={onLexicalError}
                        isFirstResponder={isFirstResponder}
                    />
                </Box>
            )}
            {!isFirstResponder && showJumpButtons && (
                <Flex
                    justify="flex-end"
                    order={isAscLayout ? 1 : undefined}
                    paddingInlineEnd="var(--arc-space-4)"
                >
                    <NarrativeJumpButtons
                        onScrollToTop={onScrollToTop}
                        onScrollToBottom={onScrollToBottom}
                        isAtTop={jumpButtonEdgeState.isAtTop}
                        isAtBottom={jumpButtonEdgeState.isAtBottom}
                        isDisabled={!jumpButtonEdgeState.isScrollable}
                        isAscLayout={isAscLayout}
                    />
                </Flex>
            )}
            {isTransmittedEvent ? (
                <Box className={styles['event-panel-transmitted-container']}>
                    <AdditionalInfoRenderer {...additionalInfoRendererProps} />
                </Box>
            ) : isFirstResponder ? (
                <Box
                    position="relative"
                    flex={1}
                    minHeight={0}
                    style={
                        showJumpButtons
                            ? ({
                                  '--jump-buttons-overlay-width': FR_JUMP_BUTTONS_RESERVED_WIDTH,
                              } as CSSProperties)
                            : undefined
                    }
                >
                    <Box height="100%" paddingTop={isReadOnlyMode ? '2' : undefined}>
                        <AdditionalInfoRenderer {...additionalInfoRendererProps} />
                    </Box>
                    {showJumpButtons && (
                        <Flex
                            direction="column"
                            justify="flex-start"
                            position="absolute"
                            top={0}
                            bottom={0}
                            right="var(--scrollbar-width)"
                            pointerEvents="none"
                        >
                            <Box pointerEvents="auto">
                                <NarrativeJumpButtons
                                    onScrollToTop={onScrollToTop}
                                    onScrollToBottom={onScrollToBottom}
                                    isAtTop={jumpButtonEdgeState.isAtTop}
                                    isAtBottom={jumpButtonEdgeState.isAtBottom}
                                    isDisabled={!jumpButtonEdgeState.isScrollable}
                                    isAscLayout={isAscLayout}
                                    isFirstResponder
                                />
                            </Box>
                        </Flex>
                    )}
                </Box>
            ) : (
                <AdditionalInfoRenderer {...additionalInfoRendererProps} />
            )}
        </VStack>
    );
}

export default EventPanelAdditionalInfos;
